import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  TextField,
  MenuItem,
  Popper,
  Paper,
  ClickAwayListener, 
  OutlinedInput,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_TIME_ZONES } from "../graphql/queries";
 

const TimezoneSelector = ({ timezoneId, onTimezoneSelect, isDisabled = false }) => {
  const [timezonesList, setTimezonesList] = useState([]);
  const [filteredTimezones, setFilteredTimezones] = useState([]);
  const [groupedTimezones, setGroupedTimezones] = useState({});
  const [currentTimes, setCurrentTimes] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTimezone, setSelectedTimezone] = useState("");


  const inputRef = useRef(null); // To dynamically calculate arrow positioning
  const { loading, error, data } = useQuery(GET_TIME_ZONES);

  useEffect(() => {
    if (data && data.getTimeZones) {
      setTimezonesList(data.getTimeZones);
      setFilteredTimezones(data.getTimeZones);
      setGroupedTimezones(groupByRegion(data.getTimeZones));

      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const initialTimezone = timezoneId || browserTimezone;
      setSelectedTimezone(initialTimezone);

      const initialTimezoneInfo = data.getTimeZones.find((tz) => Number(tz.id) === Number(initialTimezone) || (tz.timezone) === initialTimezone?.timezone); 
      if (onTimezoneSelect && initialTimezoneInfo) onTimezoneSelect(initialTimezoneInfo);
    }
  }, [data, timezoneId, onTimezoneSelect]);

  useEffect(() => {
    const updateTimes = () => {
      const newTimes = {};
      timezonesList.forEach((tz) => {
        const formatter = new Intl.DateTimeFormat("en-US", {
          timeZone: tz.timezone,
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        newTimes[tz.timezone] = formatter.format(new Date());
      });
      setCurrentTimes(newTimes);
    };

    updateTimes();
    const interval = setInterval(updateTimes, 60000);
    return () => clearInterval(interval);
  }, [timezonesList]);

  const groupByRegion = (timezones) =>
    timezones.reduce((acc, tz) => {
      const regionName = tz.region?.name || "Other";
      if (!acc[regionName]) acc[regionName] = [];
      acc[regionName].push(tz);
      return acc;
    }, {});

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = timezonesList.filter(
      (tz) =>
        tz.name.toLowerCase().includes(query) ||
        tz.timezone.toLowerCase().includes(query) ||
        tz.region?.name.toLowerCase().includes(query)
    );
    setGroupedTimezones(groupByRegion(filtered));
  };

  const handleClick = (event) => setAnchorEl(anchorEl ? null : event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelectTimezone = (timezone) => {
    setSelectedTimezone(timezone);
    const selected = timezonesList.find((tz) => tz.timezone === timezone);
    if (onTimezoneSelect && selected) onTimezoneSelect(selected);
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? "timezone-popper" : undefined;

  const selectedTimezoneName = timezonesList.find((tz) => Number(tz.id) === Number(selectedTimezone) || tz.timezone === selectedTimezone || tz.timezone === selectedTimezone?.timezone)?.name;

  return (
    <Box sx={{ width: "100%" }}>
      {loading ? (
        <Typography>Loading Timezones...</Typography>
      ) : error ? (
        <Typography>Error loading timezones: {error.message}</Typography>
      ) : (
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
                <Typography sx={{ color: "#661", fontSize: '0.7rem' }}>Timezone:</Typography>
          </Grid>
              <Grid item xs>
                <FormControl fullWidth>
                  <OutlinedInput
                    disabled={isDisabled}
                    inputRef={inputRef}
                    value={selectedTimezoneName || "Select Timezone"}
                    onClick={handleClick}
                    readOnly
                    sx={{
                      cursor: "pointer", // show hand on hover
                      "& fieldset": { border: "none" }, 
                      position: "relative", // For positioning pseudo-element
                      "&::after": {
                        content: '"\\25BC"', // Unicode for the ▼ arrow
                        position: "absolute", 
                        top: "50%",
                        transform: "translateY(-50%)", // Center vertically
                        fontSize: "0.6rem", // Smaller arrow
                        color: "rgba(0, 0, 0, 0.54)", // Match MUI text color
                        pointerEvents: "auto", // Prevent interaction
                      },
                    }}
                  />


                  <Popper
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    disablePortal 
                    modifiers={[
                      { name: "offset", options: { offset: [0, 0] } }, // Remove gap between input and dropdown
                    ]}
                    sx={{
                      width: inputRef.current ? inputRef.current.offsetWidth : "auto", // Match input width
                      zIndex: 1200,
                }}
              >
                    <Paper sx={{ maxHeight: 300, maxWidth: 400, overflowY: "auto" }}>
                      <ClickAwayListener onClickAway={handleClose}>
                        <Box>
                          <Box sx={{ padding: "8px" }}>
                            <TextField
                              placeholder="Search Timezones"
                              variant="outlined"
                              size="small"
                              fullWidth
                              value={searchQuery}
                              onChange={handleSearchChange}
                            />
                          </Box>
                          {Object.keys(groupedTimezones).map((region) => (
                            <Box key={region}>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: "bold", paddingLeft: "8px" }}
                              >
                                {region}
                              </Typography>
                              {groupedTimezones[region].map((tz) => (
                                <MenuItem
                                  key={tz.id}
                                  onClick={() => handleSelectTimezone(tz.timezone)}
                                >
                                  {tz.name} - {currentTimes[tz.timezone] || "Loading..."}
                                </MenuItem>
                              ))}
                            </Box>
                          ))}
                        </Box>
                      </ClickAwayListener>
                    </Paper>
                  </Popper>
                </FormControl>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default TimezoneSelector;
